import { useEffect, useRef } from "react";

const WheelCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let w = 0;
    let h = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    const tilt = { x: 0.35, y: 0 };
    const target = { x: 0.35, y: 0 };
    let spin = 0;

    const N = 64;
    const R = 1.0;
    const W = 0.34;
    const rimF = [];
    const rimB = [];
    for (let i = 0; i < N; i++) {
      const a = (i / N) * Math.PI * 2;
      rimF.push([R * Math.cos(a), R * Math.sin(a), W / 2]);
      rimB.push([R * Math.cos(a), R * Math.sin(a), -W / 2]);
    }
    const HN = 24;
    const HR = 0.24;
    const hub = [];
    for (let i = 0; i < HN; i++) {
      const a = (i / HN) * Math.PI * 2;
      hub.push([HR * Math.cos(a), HR * Math.sin(a), W / 2 + 0.1]);
    }
    const spokes = [];
    for (let s = 0; s < 5; s++) {
      const a = (s / 5) * Math.PI * 2;
      const a2 = a + 0.3;
      spokes.push([
        [0.1 * Math.cos(a), 0.1 * Math.sin(a), W / 2 + 0.1],
        [R * 0.96 * Math.cos(a), R * 0.96 * Math.sin(a), W / 2],
      ]);
      spokes.push([
        [0.1 * Math.cos(a2), 0.1 * Math.sin(a2), W / 2 + 0.1],
        [R * 0.96 * Math.cos(a), R * 0.96 * Math.sin(a), W / 2],
      ]);
    }

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = parent.clientWidth;
      h = parent.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    };
    resize();
    window.addEventListener("resize", resize);

    const onMouse = (e) => {
      const nx = (e.clientX / window.innerWidth) * 2 - 1;
      const ny = (e.clientY / window.innerHeight) * 2 - 1;
      target.y = nx * 0.45;
      target.x = 0.35 + ny * 0.3;
    };
    window.addEventListener("mousemove", onMouse, { passive: true });

    const project = (p, cx, cy, scale) => {
      let [x, y, z] = p;
      const cxr = Math.cos(tilt.x);
      const sxr = Math.sin(tilt.x);
      const y2 = y * cxr - z * sxr;
      let z2 = y * sxr + z * cxr;
      y = y2;
      const a = spin + tilt.y;
      const cyr = Math.cos(a);
      const syr = Math.sin(a);
      const x2 = x * cyr + z2 * syr;
      const z3 = -x * syr + z2 * cyr;
      const d = 3.4;
      const sc = d / (d + z3);
      return { sx: cx + x2 * sc * scale, sy: cy + y * sc * scale, depth: z3 };
    };

    const drawEdge = (a, b, cx, cy, scale, color, width, glow) => {
      const pa = project(a, cx, cy, scale);
      const pb = project(b, cx, cy, scale);
      const alpha = Math.max(0.08, Math.min(1, 0.85 - ((pa.depth + pb.depth) / 2) * 0.5));
      ctx.strokeStyle = color(alpha);
      ctx.lineWidth = width;
      if (glow) {
        ctx.shadowColor = "rgba(0,240,255,0.6)";
        ctx.shadowBlur = 10;
      } else {
        ctx.shadowBlur = 0;
      }
      ctx.beginPath();
      ctx.moveTo(pa.sx, pa.sy);
      ctx.lineTo(pb.sx, pb.sy);
      ctx.stroke();
    };

    const frame = () => {
      spin += 0.0038;
      tilt.x += (target.x - tilt.x) * 0.04;
      tilt.y += (target.y - tilt.y) * 0.04;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      const cx = w / 2;
      const cy = h / 2;
      const scale = Math.min(w, h) * 0.34;

      const volt = (a) => `rgba(0,240,255,${a})`;
      const silver = (a) => `rgba(226,232,240,${a * 0.8})`;
      const gold = (a) => `rgba(212,175,55,${a})`;

      for (let i = 0; i < N; i++) {
        const j = (i + 1) % N;
        drawEdge(rimF[i], rimF[j], cx, cy, scale, volt, 1.6, true);
        drawEdge(rimB[i], rimB[j], cx, cy, scale, volt, 1, false);
        if (i % 4 === 0) drawEdge(rimF[i], rimB[i], cx, cy, scale, volt, 0.8, false);
      }
      for (let i = 0; i < HN; i++) {
        drawEdge(hub[i], hub[(i + 1) % HN], cx, cy, scale, gold, 1.6, false);
      }
      for (const [a, b] of spokes) {
        drawEdge(a, b, cx, cy, scale, silver, 1.8, false);
      }
      ctx.shadowBlur = 0;
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouse);
    };
  }, []);

  return <canvas ref={canvasRef} data-testid="hero-canvas-wheel" className="absolute inset-0" aria-hidden="true" />;
};

export default WheelCanvas;
